import { useState, useEffect } from 'react';
import { Card, Button, Spinner, Badge, Row, Col } from 'react-bootstrap';
import { Edit2, RefreshCw, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { CpGpMatchedProduct } from '../../../../../models/Coupang';
import { ROUTES } from '../../../../../routes/routes';
import { verifiedImagesClient } from '../../../../../infrastructure/VerifiedImagesClient';
import { ImageGallery } from '../../../../../components/common/ImageGallery';
import { IdBadge, IdBadgeType } from '../../../../../components/ui/badge/IdBadge';
import type { SkinSeoulAdminProduct } from '../../../../../models/ProductTypes';

interface ImageProcessingSectionProps {
  matchedProduct?: CpGpMatchedProduct;
  productsBySku?: SkinSeoulAdminProduct[];
  loadingProducts?: boolean;
  onRefresh?: () => Promise<any>;
}

export const ImageProcessingSection = ({
  matchedProduct,
  productsBySku = [],
  loadingProducts = false,
  onRefresh,
}: ImageProcessingSectionProps) => {
  const navigate = useNavigate();
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [verifiedImagesFromAPI, setVerifiedImagesFromAPI] = useState<string[]>([]);

  const { data: verifiedImages, refetch: refetchVerifiedImages } = useQuery({
    queryKey: ['verifiedImages', matchedProduct?.gpProductId, matchedProduct?.cpProductId, matchedProduct?.cpItemId],
    queryFn: async () => {
      if (!matchedProduct) {
        return [];
      }
      const response = await verifiedImagesClient.getVerifiedImages({
        gpProductId: matchedProduct.gpProductId,
        cpProductId: matchedProduct.cpProductId,
        cpItemId: matchedProduct.cpItemId,
      });
      return response.success ? response.verifiedImageUrls : [];
    },
    enabled: Boolean(matchedProduct),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (verifiedImages) {
      setVerifiedImagesFromAPI(verifiedImages);
    }
  }, [verifiedImages]);

  const handleOpenEditor = () => {
    if (matchedProduct) {
      navigate(ROUTES.sourcing.matchedImageEditor(matchedProduct.cpProductId, matchedProduct.cpItemId, 'thu'), {
        state: { productData: matchedProduct },
      });
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (onRefresh) {
        await onRefresh();
      }
      await refetchVerifiedImages();
      console.info('✅ Verified images refreshed');
    } catch (error) {
      console.error('❌ Failed to refresh images:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const heroImages = matchedProduct?.cpDetailedProduct?.heroImageUrls || [];
  const pdpImages = matchedProduct?.cpDetailedProduct?.detailedImageUrls || [];
  const verifiedImagesData =
    verifiedImagesFromAPI.length > 0
      ? verifiedImagesFromAPI
      : matchedProduct?.cpDetailedProduct?.verifiedImageUrls || [];

  const verifiedPdpImages = verifiedImagesData.filter((url: string) => {
    const filename = url.split('/').pop() || '';
    return filename.startsWith('pdp_') && url.includes('/en/');
  });

  return (
    <>
      {expandedImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#000000',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
          }}
          onClick={() => setExpandedImage(null)}
        >
          <img
            src={expandedImage}
            alt="Expanded view"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              backgroundColor: 'transparent',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <Button
            variant="light"
            size="sm"
            onClick={() => setExpandedImage(null)}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
            }}
          >
            ✕ Close
          </Button>
        </div>
      )}
      <Card className="mb-4" style={{ position: 'relative' }}>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <h6 className="mb-0">Images Processing</h6>
              <Button variant="outline-secondary" size="sm" onClick={handleRefresh} disabled={refreshing}>
                {refreshing ? (
                  <>
                    <Spinner animation="border" size="sm" style={{ width: '14px', height: '14px' }} />
                  </>
                ) : (
                  <RefreshCw size={14} />
                )}
              </Button>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button variant="primary" size="sm" onClick={handleOpenEditor}>
                <Edit2 size={16} className="me-2" />
                Open Image Editor
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body style={{ position: 'relative' }}>
          <Row>
            <Col md={6}>
              <h6 className="mb-3">Source Images</h6>

              <div className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <strong style={{ fontSize: '0.9rem' }}>Thumbnail Images</strong>
                  <Badge bg="secondary">{heroImages.length}</Badge>
                </div>
                <div
                  style={{
                    padding: '0.75rem',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                  }}
                >
                  <div className="d-flex flex-wrap gap-2">
                    {heroImages.length > 0 ? (
                      heroImages.map((url, idx) => (
                        <img
                          key={`orig-thumb-${idx}`}
                          src={url}
                          alt={`Thumbnail ${idx + 1}`}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'cover',
                            border: '2px solid #dee2e6',
                            borderRadius: '6px',
                            cursor: 'pointer',
                          }}
                          onClick={() => setExpandedImage(url)}
                          title="Click to expand"
                        />
                      ))
                    ) : (
                      <small className="text-muted">No images</small>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <strong style={{ fontSize: '0.9rem' }}>PDP Images (Detailed & Verified)</strong>
                  <Badge bg="secondary">{pdpImages.length + verifiedPdpImages.length}</Badge>
                </div>
                <div
                  style={{
                    padding: '0.75rem',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                  }}
                >
                  <div className="d-flex flex-wrap gap-2">
                    {pdpImages.length > 0 ? (
                      pdpImages.map((url, idx) => (
                        <div
                          key={`orig-pdp-${idx}`}
                          style={{
                            width: '200px',
                            maxHeight: '600px',
                            border: '2px solid #dee2e6',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                          }}
                          onClick={() => setExpandedImage(url)}
                          title="Click to expand"
                        >
                          <img
                            src={url}
                            alt={`PDP ${idx + 1}`}
                            style={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: '600px',
                              display: 'block',
                              objectFit: 'cover',
                            }}
                          />
                        </div>
                      ))
                    ) : (
                      <small className="text-muted">No Original PDP images</small>
                    )}
                    {verifiedPdpImages.length > 0 &&
                      verifiedPdpImages.map((url, idx) => (
                        <div
                          key={`verified-pdp-${idx}`}
                          style={{
                            width: '200px',
                            maxHeight: '600px',
                            border: '2px solid #28a745',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                          }}
                          onClick={() => setExpandedImage(url)}
                          title="Verified Image - Click to expand"
                        >
                          <img
                            src={url}
                            alt={`Verified PDP ${idx + 1}`}
                            style={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: '600px',
                              display: 'block',
                              objectFit: 'cover',
                            }}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <h6 className="mb-3">
                Published Images <Badge bg="primary">{productsBySku.length}</Badge>
              </h6>

              {loadingProducts ? (
                <div
                  style={{
                    padding: '40px',
                    textAlign: 'center',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                  }}
                >
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 mb-0 text-muted">Loading published products...</p>
                </div>
              ) : productsBySku.length === 0 ? (
                <div
                  style={{
                    padding: '40px',
                    textAlign: 'center',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '2px dashed #dee2e6',
                  }}
                >
                  <ImageIcon size={48} className="mb-3 opacity-25" />
                  <p className="mb-0 text-muted">No published products yet</p>
                  <small className="text-muted">Products will appear here once published to WordPress</small>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  {productsBySku.map((product) => (
                    <div key={product.sku} style={{ display: 'flex', alignItems: 'start' }}>
                      <div
                        style={{
                          padding: '12px',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #dee2e6',
                        }}
                      >
                        <div className="mb-2">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <IdBadge type={IdBadgeType.SKU} value={product.sku} size="sm" />
                          </div>
                          {(product.size || product.pack || product.color) && (
                            <div style={{ fontSize: '0.8rem', color: '#333', marginBottom: '8px', fontWeight: 600 }}>
                              {[product.size, product.pack, product.color].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>

                        {/* Gallery Images Only */}
                        <div>
                          {product.productImageUrls && product.productImageUrls.length > 0 ? (
                            <ImageGallery
                              productImages={product.productImageUrls}
                              learnMoreImages={[]}
                              title={`${product.name} - Gallery`}
                              mainImageSize={{ width: 120, height: 120 }}
                              thumbnailSize={{ width: 38, height: 38 }}
                              showThumbnails={true}
                            />
                          ) : (
                            <div
                              style={{
                                width: '120px',
                                height: '120px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'white',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                              }}
                            >
                              <ImageIcon size={32} className="text-muted opacity-25" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Published PDP Images - Show only first SKU's images */}
              {productsBySku.length > 0 &&
                (() => {
                  const firstSkuPdpImages = productsBySku[0]?.learnMoreUrl || [];

                  return firstSkuPdpImages.length > 0 ? (
                    <div className="mt-4">
                      <h6 className="mb-3">
                        Published PDP Images <Badge bg="info">{firstSkuPdpImages.length}</Badge>
                      </h6>
                      <div
                        style={{
                          padding: '12px',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #dee2e6',
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: '12px',
                        }}
                      >
                        {firstSkuPdpImages.map((url, pdpIdx) => (
                          <div
                            key={`pdp-${pdpIdx}`}
                            style={{
                              width: '100%',
                              aspectRatio: '1 / 2',
                              cursor: 'pointer',
                              border: '2px solid #0d6efd',
                              borderRadius: '6px',
                              overflow: 'hidden',
                              position: 'relative',
                            }}
                            onClick={() => setExpandedImage(url)}
                            title="PDP Image - Click to expand"
                          >
                            <img
                              src={url}
                              alt={`PDP ${pdpIdx + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};
